import React, { useState, useEffect } from 'react';
import { Plug, Shield, Package, CheckCircle, XCircle, PlayCircle, Loader } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useTenantProtection } from '../../../hooks/useTenantProtection';

const EndpointList = () => {
  const { getProtectedTenantCode } = useTenantProtection();
  
  const [endpoints, setEndpoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEndpoint, setSelectedEndpoint] = useState(null);
  const [testingEndpoint, setTestingEndpoint] = useState(null);
  const [testResults, setTestResults] = useState({});

  useEffect(() => {
    loadEndpoints();
  }, []);

  const loadEndpoints = async () => {
    try {
      setLoading(true);
      const tenantCode = getProtectedTenantCode();
      const response = await axios.get('/api/automation/endpoints', {
        headers: { 'X-Tenant-Code': tenantCode },
      });
      setEndpoints(response.data);
    } catch (error) {
      console.error('Failed to load endpoints:', error);
      toast.error('Failed to load endpoints');
    } finally {
      setLoading(false);
    }
  };

  const testEndpoint = async (endpoint, e) => {
    e.stopPropagation(); // Prevent card selection
    
    try {
      setTestingEndpoint(endpoint.EndpointID);
      const tenantCode = getProtectedTenantCode();
      
      // Call endpoint validation API
      const response = await axios.post(
        `/api/automation/endpoints/${endpoint.EndpointID}/validate`,
        { test: true }, // Empty test parameters
        { headers: { 'X-Tenant-Code': tenantCode } }
      );
      
      setTestResults(prev => ({
        ...prev,
        [endpoint.EndpointID]: {
          status: 'success',
          message: 'Endpoint is valid and ready',
          timestamp: new Date().toISOString(),
          data: response.data
        }
      }));
      
      toast.success(`Endpoint "${endpoint.EndpointName}" is valid`);
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message || 'Endpoint test failed';
      
      setTestResults(prev => ({
        ...prev,
        [endpoint.EndpointID]: {
          status: 'error',
          message: errorMessage,
          timestamp: new Date().toISOString()
        }
      }));
      
      toast.error(`Endpoint test failed: ${errorMessage}`);
    } finally {
      setTestingEndpoint(null);
    }
  };

  const getMethodColor = (method) => {
    switch (method) {
      case 'POST':
        return 'bg-green-100 text-green-800';
      case 'GET':
        return 'bg-blue-100 text-blue-800';
      case 'PUT':
        return 'bg-yellow-100 text-yellow-800';
      case 'DELETE':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Registered Endpoints</h2>
      </div>

      {/* Endpoint Cards */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {endpoints.map((endpoint) => (
          <div
            key={endpoint.EndpointID}
            className={`bg-white rounded-lg border-2 p-6 cursor-pointer transition-all ${
              selectedEndpoint?.EndpointID === endpoint.EndpointID
                ? 'border-purple-500 shadow-lg'
                : 'border-gray-200 hover:border-gray-300 hover:shadow'
            }`}
            onClick={() => setSelectedEndpoint(
              selectedEndpoint?.EndpointID === endpoint.EndpointID ? null : endpoint
            )}
          >
            {/* Header */}
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-3 flex-1">
                <div className={`p-2 rounded-lg ${endpoint.IsSystemEndpoint ? 'bg-blue-100' : 'bg-purple-100'}`}>
                  {endpoint.IsSystemEndpoint ? (
                    <Shield className="h-6 w-6 text-blue-600" />
                  ) : (
                    <Package className="h-6 w-6 text-purple-600" />
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-medium text-gray-900">
                    {endpoint.EndpointName}
                  </h3>
                  <div className="mt-1 flex items-center space-x-2">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getMethodColor(endpoint.HttpMethod)}`}>
                      {endpoint.HttpMethod}
                    </span>
                    {endpoint.IsSystemEndpoint && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        System
                      </span>
                    )}
                    {endpoint.IsActive ? (
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    ) : (
                      <XCircle className="h-4 w-4 text-red-500" />
                    )}
                  </div>
                </div>
              </div>
              <div className="flex flex-col items-end space-y-2">
                <div className="text-sm text-gray-500">
                  {endpoint.JobCount || 0} jobs
                </div>
                <button
                  onClick={(e) => testEndpoint(endpoint, e)}
                  disabled={testingEndpoint === endpoint.EndpointID}
                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Test endpoint validation"
                >
                  {testingEndpoint === endpoint.EndpointID ? (
                    <>
                      <Loader className="h-3 w-3 mr-1 animate-spin" />
                      Testing...
                    </>
                  ) : (
                    <>
                      <PlayCircle className="h-3 w-3 mr-1" />
                      Test
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Description */}
            {endpoint.Description && (
              <p className="mt-3 text-sm text-gray-600">
                {endpoint.Description}
              </p>
            )}

            {/* URL */}
            <div className="mt-3">
              <div className="text-xs font-medium text-gray-500 mb-1">Endpoint URL</div>
              <code className="text-xs bg-gray-100 px-2 py-1 rounded block overflow-x-auto">
                {endpoint.EndpointURL}
              </code>
            </div>

            {/* Test Results */}
            {testResults[endpoint.EndpointID] && (
              <div className={`mt-3 p-3 rounded-lg ${
                testResults[endpoint.EndpointID].status === 'success' 
                  ? 'bg-green-50 border border-green-200' 
                  : 'bg-red-50 border border-red-200'
              }`}>
                <div className="flex items-start space-x-2">
                  {testResults[endpoint.EndpointID].status === 'success' ? (
                    <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                  ) : (
                    <XCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                  )}
                  <div className="flex-1">
                    <div className={`text-xs font-medium ${
                      testResults[endpoint.EndpointID].status === 'success' 
                        ? 'text-green-900' 
                        : 'text-red-900'
                    }`}>
                      {testResults[endpoint.EndpointID].message}
                    </div>
                    <div className={`text-xs mt-1 ${
                      testResults[endpoint.EndpointID].status === 'success' 
                        ? 'text-green-700' 
                        : 'text-red-700'
                    }`}>
                      Tested: {new Date(testResults[endpoint.EndpointID].timestamp).toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Parameters - Show if expanded */}
            {selectedEndpoint?.EndpointID === endpoint.EndpointID && endpoint.RequiredParameters && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="text-xs font-medium text-gray-500 mb-2">Required Parameters</div>
                <div className="bg-gray-50 rounded p-3 text-xs font-mono overflow-x-auto">
                  <pre className="whitespace-pre-wrap">
                    {JSON.stringify(JSON.parse(endpoint.RequiredParameters), null, 2)}
                  </pre>
                </div>
              </div>
            )}

            {/* Created By */}
            {endpoint.CreatedByName && (
              <div className="mt-3 text-xs text-gray-500">
                Created by: {endpoint.CreatedByName}
              </div>
            )}
          </div>
        ))}
      </div>

      {endpoints.length === 0 && (
        <div className="text-center py-12 bg-white rounded-lg border-2 border-dashed border-gray-300">
          <Plug className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No endpoints</h3>
          <p className="mt-1 text-sm text-gray-500">
            No endpoints are registered in the system yet.
          </p>
        </div>
      )}

      {/* Info Box */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Shield className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium">About Endpoints</p>
            <p className="mt-1">
              Endpoints are the actions that automation jobs can execute. System endpoints are
              built-in and cannot be modified. Custom endpoints can be added through the API.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EndpointList;

